import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Breadcrumbs,
  Table,
  Column,
  SvgIcon,
} from '@elementary/common-ui/components';
import { ToggleColumnsMenu } from '@elementary/common-ui/components/Table';
import Button from '@elementary/common-ui/components/Button';
import { ReactComponent as ArrowLeft } from '@elementary/common-ui/assets/arrow-left.svg';
import {
  AgentTestChangeRowIdentifiers,
  AgentTestConfigPlan,
  AgentTestOperation,
  AgentCodeTestDraftPr,
} from '@elementary/common-ui/types';
import { useNavigate } from 'react-router-dom';
import { RecoilState, useRecoilState } from 'recoil';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useToggleTreeView from '@elementary/common-ui/hooks/useToggleTreeView';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import useTestManagementTableData from '../../hooks/useTestManagementTableData';
import useTestManagementTableColumns from '../../hooks/useTestManagementTableColumns';
import PlanPreviewTestNameRenderer from './PlanPreviewTestNameRenderer';
import useConfigPreviewColumnBuilders from './useConfigPreviewColumnBuilders';
import {
  BackButton,
  DraftPrAdditions,
  DraftPrBanner,
  DraftPrDeletions,
  DraftPrLink,
  DraftPrNumbers,
  DraftPrParagraph,
  DraftPrSummary,
  EmptyState,
  Header,
  HeaderActions,
  HeaderNavigation,
  SelectedCountLabel,
  SkipSummary,
  TableContainer,
  Wrapper,
} from './styles';
import { BasePreviewRow } from './types';

export type AgentConfigPreviewShellProps<
  TChange extends AgentTestChangeRowIdentifiers,
  TRow extends BasePreviewRow<TChange>,
> = {
  planAtom: RecoilState<AgentTestConfigPlan<TChange> | null>;
  isCreateChange: (change: TChange) => boolean;
  buildCreatePreviewRow: (change: TChange) => TRow;
  isCreateRowDataLoading: boolean;
  getPresentFields: (plan: AgentTestConfigPlan<TChange>) => Set<string>;
  buildDynamicColumns: (params: {
    plan: AgentTestConfigPlan<TChange>;
    presentFields: Set<string>;
    columnBuilders: ReturnType<typeof useConfigPreviewColumnBuilders<TRow>>;
  }) => Column<TRow>[];
  getSubmitLabel: (
    operation: AgentTestOperation,
    selectedCount: number,
    selectedChanges: TChange[],
  ) => string;
  breadcrumbLabels: Record<AgentTestOperation, string>;
  storageKey: string;
  onRowClick?: (row: TRow) => void;
  draftPr?: AgentCodeTestDraftPr | null;
};

const AgentConfigPreviewShell = <
  TChange extends AgentTestChangeRowIdentifiers,
  TRow extends BasePreviewRow<TChange>,
>({
  planAtom,
  isCreateChange,
  buildCreatePreviewRow,
  isCreateRowDataLoading,
  getPresentFields,
  buildDynamicColumns,
  getSubmitLabel,
  breadcrumbLabels,
  storageKey,
  onRowClick,
  draftPr,
}: AgentConfigPreviewShellProps<TChange, TRow>) => {
  const [plan, setPlan] = useRecoilState(planAtom);
  const navigate = useNavigateWithEnv();
  const navigateBack = useNavigate();
  const envId = useSelectedEnv();
  const operation = plan?.operation ?? 'update';
  const isCreateOperation = operation === 'create';
  const isDeleteOperation = operation === 'delete';

  const { setTreeViewCollapsed } = useToggleTreeView({
    restoreOnUnmount: true,
  });

  useEffect(() => {
    setTreeViewCollapsed(true);
  }, [setTreeViewCollapsed]);

  const { data: testData, isLoading: isTestDataLoading } =
    useTestManagementTableData({
      ignoreTreeView: true,
    });

  const isLoading = isCreateOperation
    ? isCreateRowDataLoading
    : isTestDataLoading;

  const envRef = useRef(envId);
  useEffect(() => {
    if (envRef.current === envId) return;
    envRef.current = envId;
    setPlan(null);
  }, [envId, setPlan]);

  const mergedData = useMemo<TRow[]>(() => {
    if (!plan) return [];

    if (plan.operation === 'create') {
      return plan.changes.reduce<TRow[]>((acc, change) => {
        if (!isCreateChange(change)) return acc;
        acc.push(buildCreatePreviewRow(change));
        return acc;
      }, []);
    }

    const testMap = new Map(testData.map((t) => [t.id, t]));
    return plan.changes.reduce<TRow[]>((acc, change) => {
      if (isCreateChange(change)) return acc;
      if (!('testId' in change) || !change.testId) return acc;
      const test = testMap.get(change.testId);
      if (test) {
        acc.push({ ...test, configChange: change } as TRow);
      }
      return acc;
    }, []);
  }, [plan, testData, isCreateChange, buildCreatePreviewRow]);

  const presentFields = useMemo(
    () => (plan ? getPresentFields(plan) : new Set<string>()),
    [plan, getPresentFields],
  );

  const breadcrumbs = useMemo(
    () => [
      {
        label: 'Test Configuration',
        onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          e.preventDefault();
          navigate('/test-management');
        },
      },
      breadcrumbLabels[operation],
    ],
    [navigate, operation, breadcrumbLabels],
  );

  const hasResponded = !!plan?.userResponse || !!plan?.agentAcknowledged;
  const actionsDisabled = hasResponded || !!plan?.isReadOnly;
  const isSelectAll = plan?.isSelectAll ?? true;
  const excludedItems = plan?.excludedItems ?? [];
  const selectedItems = plan?.selectedItems ?? [];

  const mountGuard = useRef(true);

  useEffect(() => {
    mountGuard.current = false;
  }, []);

  const setIsSelectAll = useCallback(
    (val: boolean) =>
      setPlan((prev) =>
        prev
          ? { ...prev, isSelectAll: val, excludedItems: [], selectedItems: [] }
          : prev,
      ),
    [setPlan],
  );

  const setExcludedItems = useCallback(
    (val: string[]) => {
      if (mountGuard.current) return;
      setPlan((prev) => (prev ? { ...prev, excludedItems: val } : prev));
    },
    [setPlan],
  );

  const setSelectedItems = useCallback(
    (val: string[]) => {
      if (mountGuard.current) return;
      setPlan((prev) => (prev ? { ...prev, selectedItems: val } : prev));
    },
    [setPlan],
  );

  const selectedRows = mergedData.filter((row) =>
    isSelectAll
      ? !excludedItems.includes(row.id)
      : selectedItems.includes(row.id),
  );

  const selectedCount = selectedRows.length;

  const hasSelection = selectedCount > 0;

  const configColumns = useTestManagementTableColumns(
    mergedData,
    'configuration',
  );

  const columnBuilders = useConfigPreviewColumnBuilders<TRow>();

  const dynamicColumns = useMemo<Column<TRow>[]>(() => {
    if (!plan || isDeleteOperation) return [];
    return buildDynamicColumns({
      plan,
      presentFields,
      columnBuilders,
    });
  }, [
    plan,
    isDeleteOperation,
    buildDynamicColumns,
    presentFields,
    columnBuilders,
  ]);

  const showRowEdit = !!onRowClick && !isDeleteOperation && !actionsDisabled;

  const columns = useMemo<Column<TRow>[]>(() => {
    const mapped = configColumns
      .filter((col) => !presentFields.has(col.id))
      .map((col) => {
        if (col.id === 'name') {
          return {
            ...col,
            filterable: false,
            nonToggleable: true,
            showValueWhenEmpty: true,
            renderer: (row: TRow) => (
              <PlanPreviewTestNameRenderer
                row={row}
                showEdit={showRowEdit}
                onEdit={onRowClick}
              />
            ),
          };
        }
        return {
          ...col,
          filterable: false,
        };
      });
    const [first, ...rest] = mapped;
    return [first, ...dynamicColumns, ...rest];
  }, [configColumns, dynamicColumns, onRowClick, presentFields, showRowEdit]);

  const tableKey = useMemo(
    () => `${operation}:${Array.from(presentFields).sort().join(',')}`,
    [operation, presentFields],
  );

  const handleSubmit = () => {
    setPlan((prev) => (prev ? { ...prev, userResponse: 'confirmed' } : prev));
    navigate('/test-management');
  };

  const handleCancel = () => {
    setPlan((prev) => (prev ? { ...prev, userResponse: 'rejected' } : prev));
    navigate('/test-management');
  };

  const handleBack = useCallback(() => {
    navigateBack(-1);
  }, [navigateBack]);

  const submitLabel = getSubmitLabel(
    operation,
    selectedCount,
    selectedRows.map((row) => row.configChange),
  );

  return (
    <Wrapper>
      <Header>
        <HeaderNavigation>
          <BackButton
            onClick={handleBack}
            aria-label="Back"
            size="small"
            sx={{ p: '4px' }}
          >
            <SvgIcon width={16} height={14} fillColor="icon.faint">
              <ArrowLeft />
            </SvgIcon>
          </BackButton>
          <Breadcrumbs items={breadcrumbs} showTreeButton={false} />
        </HeaderNavigation>
        {plan && !isLoading && (
          <HeaderActions>
            {!actionsDisabled && (
              <SelectedCountLabel>
                {`${pluralizeString(selectedCount, 'test', 'tests')} selected`}
              </SelectedCountLabel>
            )}
            <Button
              variant="outlined"
              size="extraSmall"
              onClick={handleCancel}
              disabled={actionsDisabled}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              size="extraSmall"
              onClick={handleSubmit}
              disabled={actionsDisabled || !hasSelection}
            >
              {submitLabel}
            </Button>
            <ToggleColumnsMenu columns={columns} localStorageKey={storageKey} />
          </HeaderActions>
        )}
      </Header>
      {plan?.skipSummary && !isLoading && (
        <SkipSummary>{plan.skipSummary}</SkipSummary>
      )}
      {plan && !isLoading && draftPr && (
        <DraftPrBanner>
          <DraftPrParagraph>
            {draftPr.changedFiles != null && (
              <DraftPrSummary>
                <span>There are</span>
                <DraftPrNumbers>
                  {draftPr.deletions != null && (
                    <DraftPrDeletions>-{draftPr.deletions}</DraftPrDeletions>
                  )}
                  {draftPr.additions != null && (
                    <DraftPrAdditions>+{draftPr.additions}</DraftPrAdditions>
                  )}
                </DraftPrNumbers>
                <span>
                  {`line changes across ${pluralizeString(
                    draftPr.changedFiles,
                    'file',
                    'files',
                  )}.`}
                </span>
              </DraftPrSummary>
            )}
            <DraftPrLink
              href={draftPr.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              See code changes in the PR →
            </DraftPrLink>
          </DraftPrParagraph>
        </DraftPrBanner>
      )}
      {plan && !isLoading ? (
        <TableContainer>
          <Table
            key={tableKey}
            data={mergedData}
            columns={columns}
            idKey="id"
            hidePagination
            localStorageKey={storageKey}
            selectionColumn
            selectionDisabled={actionsDisabled}
            bottomScrollPadding={60}
            isSelectAll={isSelectAll}
            onSelectAllChange={actionsDisabled ? undefined : setIsSelectAll}
            selectedItems={selectedItems}
            onSelectItem={actionsDisabled ? undefined : setSelectedItems}
            excludedItems={excludedItems}
            onExcludeItem={actionsDisabled ? undefined : setExcludedItems}
          />
        </TableContainer>
      ) : isLoading ? (
        <EmptyState>Loading test data...</EmptyState>
      ) : (
        <EmptyState>No preview data available.</EmptyState>
      )}
    </Wrapper>
  );
};

export default AgentConfigPreviewShell;
